import { ConsoleResourceSelect } from '@/console/features/rbac/components/resource-selectors/console-resource-select';
import { consoleRbacPermissionChecks } from '@/console/features/rbac/config/console-rbac-permission-checks';
import { ResourceTypeEnum } from '@/shared/components/ui/resource-icon/resource-icon.types';
import { POLICIES } from '@/shared/features/rbac/components/policies/policies.constants';
import {
  TRbacConfig,
  TRbacResourceSelectors,
  TRbacTenantSettings,
} from '@/shared/features/rbac/context/rbac-config-context.types';
import { useFeatureFlag } from '@/shared/hooks/use-feature-flag/use-feature-flag';
import {
  FEATURE_CUSTOM_FIELDS,
  FEATURE_NAME_WEBHOOK,
  FEATURE_SECURE_REMOTE_ACCESS,
  FEATURE_VULNERABILITIES,
} from '@/shared/hooks/use-feature-flag/use-feature-flag.constants';
import {
  useCreateUserMutation,
  useCreateUserRoleMutation,
  useDeleteRoleMutation,
  useDeleteUserMutation,
  useLinkRoleToUserMutation,
  useReadAllRolesQuery,
  useReadAllUsersBaseQuery,
  useReadAllUsersQuery,
  useReadPoliciesForOwnUserQuery,
  useReadPoliciesForUserQuery,
  useReadRoleQuery,
  useReadTenantQuery,
  useReadUserQuery,
  useReadUserRoleLinksForRoleQuery,
  useReadUserRoleLinksForUserQuery,
  useReadUsersForRoleQuery,
  useReadUserUiPreferencesQuery,
  useRemoveRoleFromUserMutation,
  useUpdateUserMutation,
  useUpdateUserRoleLinkMutation,
  useUpdateUserRoleMutation,
  useUpdateUserUiPreferencesMutation,
} from '@/shared/redux/api/ident/enhanced-ident-api';

const useConsoleTenantSettings = (): TRbacTenantSettings => {
  const { data, isLoading } = useReadTenantQuery();

  return {
    ssoEnabled: data?.readTenant?.ssoEnabled ?? false,
    manageSsoUsersInSda: data?.readTenant?.manageSsoUsersInSda ?? false,
    maxUserRoleExpirationDays: data?.readTenant?.maxUserRoleExpirationDays,
    companyName: data?.readTenant?.companyName,
    ownerId: data?.readTenant?.ownerId,
    isLoadingTenant: isLoading,
  };
};

const RESOURCE_TYPE_FEATURE_FLAGS: Partial<Record<ResourceTypeEnum, string>> = {
  [ResourceTypeEnum.CustomField]: FEATURE_CUSTOM_FIELDS,
  [ResourceTypeEnum.Vulnerabilities]: FEATURE_VULNERABILITIES,
  [ResourceTypeEnum.Webhook]: FEATURE_NAME_WEBHOOK,
  [ResourceTypeEnum.Workstation]: FEATURE_SECURE_REMOTE_ACCESS,
};

const useConsoleIsResourceTypeEnabled = () => {
  const { isFeatureFlagEnabled } = useFeatureFlag();

  return (resourceType: ResourceTypeEnum) => {
    const featureFlag = RESOURCE_TYPE_FEATURE_FLAGS[resourceType];

    return !featureFlag || isFeatureFlagEnabled(featureFlag);
  };
};

const CONSOLE_RESOURCES = [
  ResourceTypeEnum.Device,
  ResourceTypeEnum.Gateway,
  ResourceTypeEnum.Document,
  ResourceTypeEnum.Integration,
  ResourceTypeEnum.License,
  ResourceTypeEnum.Vault,
  ResourceTypeEnum.Project,
  ResourceTypeEnum.Pipeline,
  ResourceTypeEnum.Webhook,
  ResourceTypeEnum.Workstation,
];

const resourceSelectors = Object.fromEntries(
  CONSOLE_RESOURCES.map((resourceType) => [
    resourceType,
    ConsoleResourceSelect,
  ]),
) as TRbacResourceSelectors;

export const consoleRbacConfig: TRbacConfig = {
  api: {
    useReadAllRolesQuery,
    useReadRoleQuery,
    useReadAllUsersBaseQuery,
    useReadAllUsersQuery,
    useReadUserQuery,
    useReadUsersForRoleQuery,
    useReadUserRoleLinksForUserQuery,
    useReadUserRoleLinksForRoleQuery,
    useReadPoliciesForUserQuery,
    useReadOwnUserRolesQuery: useReadPoliciesForOwnUserQuery,
    useCreateUserMutation,
    useUpdateUserMutation,
    useDeleteUserMutation,
    useCreateUserRoleMutation,
    useUpdateUserRoleMutation,
    useDeleteRoleMutation,
    useLinkRoleToUserMutation,
    useRemoveRoleFromUserMutation,
    useUpdateUserRoleLinkMutation,
    useReadUserUiPreferencesQuery,
    useUpdateUserUiPreferencesMutation,
  },
  policies: POLICIES,
  permissionActions: {
    createUser: 'ident:createUser',
    readUser: 'ident:readUser',
    updateUser: 'ident:updateUser',
    deleteUser: 'ident:deleteUser',
    createUserRole: 'ident:createUserRole',
    readUserRole: 'ident:readUserRole',
    updateUserRole: 'ident:updateUserRole',
    deleteUserRole: 'ident:deleteUserRole',
    linkUserRole: 'ident:linkUserRole',
    unlinkUserRole: 'ident:unlinkUserRole',
  },
  resourceSelectors,
  resourceAggregations: { group: true, tags: true },
  useTenantSettings: useConsoleTenantSettings,
  useIsResourceTypeEnabled: useConsoleIsResourceTypeEnabled,
  rbacPermissionChecks: consoleRbacPermissionChecks,
};
